/**
 * Allowed Conflicts
 *
 * These are the commands that are allowed to have the same hotkey and will not throw an error.
 * Each array contains commands that are allowed to conflict with each other.
 *
 * This is commonly used for commands across different sections of the app, for example:
 * - Finder.inProjectSearch, Viewing.inTranscriptSearch, and Viewing.inDocumentSearch all may use
 * Meta + F to search, but because they are applicable in different contexts, they are allowed to
 * conflict with each other on the settings page.
 */
export const CONFLICTS_ALLOWED = [
  ['finder.rename', 'comments.replyToComment'],
  [
    'finder.inProjectSearch',
    'viewing.inTranscriptSearch',
    'viewing.inDocumentSearch',
  ],
  ['finder.grid.right', 'viewing.next'],
  ['finder.grid.left', 'viewing.previous'],
  ['viewing.togglePlaying', 'viewing.pan'],
  ['viewing.previousFrame', 'finder.grid.leftAndSelect'],
  ['viewing.nextFrame', 'finder.grid.rightAndSelect'],
  ['viewing.goToPreviousPage', 'viewing.scrollUp', 'finder.up'],
  ['viewing.goToNextPage', 'viewing.scrollDown', 'finder.down'],
  ['viewing.nextFrame', 'finder.grid.right'],
  ['viewing.previousFrame', 'finder.grid.left'],
  ['viewing.next', 'finder.grid.rightAndSelect'],
  ['viewing.previous', 'finder.grid.leftAndSelect'],
  // Shift+ArrowLeft / Shift+ArrowRight: 10-frame jump in viewer vs.
  // range-select in finder — different panel contexts.
  ['viewing.decrementByFrames', 'finder.grid.leftAndSelect'],
  ['viewing.incrementByFrames', 'finder.grid.rightAndSelect'],
  // S: compare versions in the viewer vs. compare versions in the finder —
  // different panel contexts, same conceptual action.
  ['finder.compareVersions', 'viewing.compareVersions'],
  // Enter: drill into a folder from the asset carousel vs. open the selected
  // asset/folder from the grid — different panel contexts, same conceptual
  // "open" action.
  ['assetCarousel.enterFolder', 'finder.grid.open'],
];

/**
 * Wildcard allow-list — command ids that are skipped entirely during conflict
 * detection. Used for non-customizable tool shortcuts (marquee zoom on `z`,
 * pan on Space) that the user might inadvertently land on while customizing
 * another shortcut. Without this, a user binding e.g. `z` would be blocked
 * by the marquee tool even though they have no way to move marquee off `z`.
 */
export const CONFLICTS_ALLOWED_WILDCARD: ReadonlyArray<string> = [
  'viewing.setMarquee',
  'viewing.pan',
  // The keys that move, finish and cancel moving a draft pin. They are not
  // customizable, only apply while a pin is shown, and share keys with many
  // viewer and finder shortcuts.
  'pinPlacement.finishMove',
  'pinPlacement.moveLeft',
  'pinPlacement.moveRight',
  'pinPlacement.moveUp',
  'pinPlacement.moveDown',
  ...(['Left', 'Right', 'Up', 'Down'] as const).flatMap((name) => [
    `pinPlacement.largeSteps${name}`,
    `pinPlacement.preciseSteps${name}`,
  ]),
  'pinPlacement.cancelMove',
];

/**
 * localStorage key used by appComponentsStorage to persist custom keyboard shortcuts.
 *
 * @todo (PLAY-5525): Persist the customized shortcuts to backend.
 */
export const CUSTOMIZED_SHORTCUTS_STORAGE_KEY =
  'frame-io--custom-keyboard-shortcuts';
